import { setIsEqual, arrayIsEqual } from '../util.js';

const maxIndent = 3;

/**
 * Perform a post-order depth-first traversal of the tree.
 */
function visit(options, f) {
  for (const option of options) {
    if (option.children) {
      visit(option.children, f);
    }
    f(option);
  }
}

/**
 * Perform a depth-first search of the tree.
 */
function find(options, value) {
  for (const option of options) {
    if (option.value === value) {
      return option;
    }
    if (option.children) {
      const maybe = find(option.children, value);
      if (maybe) {
        return maybe;
      }
    }
  }
  return null;
}

/**
 * Flattens the tree, maintaining information about indent level and each node's parent
 */
function flatten(options) {
  return function _flatten(options, currentIndent, parent, visible) {
    return options.flatMap(option => {
      const expanded = !!option.expanded;
      const flat = {
        ...option,
        indentLevel: currentIndent,
        parent,
        visible,
        original: option,
        isLeaf: !option.children
      };
      if (!option.children) {
        return [flat];
      }
      const nextIndent = Math.min(maxIndent, currentIndent + 1);
      return [flat, ..._flatten(option.children, nextIndent, flat, visible && expanded)];
    });
  }(options, 0, undefined, true);
}

/**
 * Returns an updated state for the tree given a modifying event.
 */
function reducer(state, action) {
  const next = structuredClone(state);
  const flatOptions = flatten(next.options);
  let nextValue = new Set(next.value);
  let performSort = false;
  switch (action.type) {
    case 'select-all':
      {
        const all = getAllValues(next.options);
        const allSelected = setIsEqual(all, nextValue);
        if (allSelected) {
          nextValue = new Set();
        } else {
          nextValue = all;
        }
      }
      break;
    case 'add':
      {
        const option = flatOptions.find(x => x.value === action.value);
        if (!option) {
          throw new Error(`This tree does not contain an option with value: ${action.value}`);
        }

        // the option in question and all of its children become checked
        (function addChildren(option) {
          nextValue.add(option.value);
          if (option.children != null) {
            for (const child of option.children) {
              addChildren(child);
            }
          }
        })(option.original);

        // set the ancestors of the option to checked as long as all of the children
        // of each ancestor are checked
        for (let parent = option.parent; parent != null; parent = parent.parent) {
          if (parent.children.every(child => nextValue.has(child.value))) {
            nextValue.add(parent.value);
          } else {
            break;
          }
        }
      }
      break;
    case 'remove':
      {
        const option = flatOptions.find(x => x.value === action.value);
        if (!option) {
          throw new Error(`This tree does not contain an option with value: ${action.value}`);
        }

        // the option in question and all of its children become unchecked
        (function removeChildren(option) {
          nextValue.delete(option.value);
          if (option.children != null) {
            for (const child of option.children) {
              removeChildren(child);
            }
          }
        })(option.original);

        // set the ancestors of the option to unchecked
        for (let parent = option.parent; parent != null; parent = parent.parent) {
          nextValue.delete(parent.value);
        }
      }
      break;
    case 'toggle':
      {
        const option = flatOptions.find(x => x.value === action.value);
        if (!option) {
          throw new Error(`This tree does not contain an option with value: ${action.value}`);
        }

        // the option in question changes its expanded state.
        option.original.expanded = !option.original.expanded;
      }
      break;
    case 'open':
      performSort = true;
      break;
    case 'add-single':
      nextValue = new Set([action.value]);
      break;
    case 'remove-single':
      nextValue = new Set();
      break;
  }

  // determine indeterminate states
  visit(next.options, option => {
    option.indeterminate = option.children != null && !nextValue.has(option.value) && option.children.some(child => child.indeterminate || nextValue.has(child.value));
  });
  if (performSort) {
    // all options are sorted by selection state
    const comparator = createSelectionStateComparator(nextValue);
    visit(next.options, option => {
      option.children?.sort(comparator);
    });
    next.options.sort(comparator);
  }

  // give them sorted values always
  const nextAsArray = [...nextValue].sort((a, b) => a.localeCompare(b));

  // if it matches the original, return the original reference
  // so things like `useMemo` and `useEffect` can work properly
  next.value = arrayIsEqual(state.value, nextAsArray) ? state.value : nextAsArray;
  return next;
}
function areAllSelected(options, values) {
  const all = getAllValues(options);
  return setIsEqual(all, new Set(values));
}
function getAllValues(options) {
  const values = new Set();
  visit(options, x => values.add(x.value));
  return values;
}
function createSelectionStateComparator(values) {
  return (a, b) => {
    const aChecked = values.has(a.value);
    const bChecked = values.has(b.value);
    if (aChecked === bChecked && a.indeterminate === b.indeterminate) {
      return 0;
    }

    // Sort any checked options over any unchecked or indeterminate options
    if (aChecked) {
      return -1;
    }
    if (bChecked) {
      return 1;
    }

    // Sort any indeterminate options over any unchecked options
    if (a.indeterminate) {
      return -1;
    }
    if (b.indeterminate) {
      return 1;
    }
    return 0;
  };
}

export { areAllSelected, find, flatten, reducer, visit };
